import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Brain,
  Code,
  GitGraph,
  Users,
  GraduationCap,
  Award,
  Building,
  Globe,
  Book,
  Zap,
  Lightbulb,
  Trophy,
  Target,
  Rocket,
  Shield,
  TrendingUp,
  CheckCircle,
  ChevronRight
} from "lucide-react";

const AboutUs = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* ---------- data ---------- */
  const metrics = [
    { number: "100+", label: "Enterprise implementations" },
    { number: "20+", label: "Countries served" },
    { number: "95%", label: "Client satisfaction rate" },
    { number: "3.2x", label: "Average ROI" }
  ];

  const coreServices = [
    {
      icon: <Rocket className="w-10 h-10 text-blue-600" />,
      title: "Custom AI Solutions",
      description:
        "End-to-end development of enterprise AI systems tailored to your specific business challenges and infrastructure."
    },
    {
      icon: <GitGraph className="w-10 h-10 text-purple-600" />,
      title: "System Integration",
      description:
        "Seamless integration of AI solutions with existing enterprise systems using Model Context Protocol (MCP) and modern APIs."
    },
    {
      icon: <Shield className="w-10 h-10 text-indigo-600" />,
      title: "Enterprise Security",
      description:
        "Bank-grade security implementations with compliance for GDPR, HIPAA, and industry-specific regulations."
    },
    {
      icon: <TrendingUp className="w-10 h-10 text-green-600" />,
      title: "Continuous Optimization",
      description:
        "24/7 monitoring, performance optimization, and iterative improvements to maximize your AI investment."
    }
  ];

  const solutions = [
    {
      title: "Real-Time Translation Platform",
      description: "Breaking language barriers in professional services with 20+ language support",
      impact: "Enabled global service delivery for financial and healthcare sectors",
      icon: <Globe className="w-8 h-8" />
    },
    {
      title: "Industrial AI Assistant",
      description: "Intelligent document processing for manufacturing operations",
      impact: "85% reduction in technical query resolution time",
      icon: <Brain className="w-8 h-8" />
    },
    {
      title: "Video Translation Suite",
      description: "AI-powered video localization with voice cloning technology",
      impact: "10x faster global content distribution",
      icon: <Zap className="w-8 h-8" />
    }
  ];

  const team = [
    {
      name: "Ritesh Vajariya",
      role: "Founder & CEO",
      image:
        "https://aigurufiles.s3.amazonaws.com/images/Ritesh-thumbnail.png",
      bio:
        "Silicon Valley veteran with 15+ years building enterprise AI solutions. Former Head of Generative AI Strategy at Cerebras Systems and AWS leader for SageMaker and Amazon Bedrock.",
      achievements: [
        "Led AI implementations for Fortune 500 companies",
        "Architected solutions processing billions of transactions",
        "Published research on enterprise AI adoption"
      ],
      linkedin: "https://www.linkedin.com/in/vajariya"
    }
  ];

  const timeline = [
    {
      year: "2018",
      title: "Foundation",
      description: "Started with a vision to democratize enterprise AI"
    },
    {
      year: "2020",
      title: "First Major Implementation",
      description: "Deployed real-time translation platform for financial services"
    },
    {
      year: "2022",
      title: "Global Expansion",
      description: "Extended operations to 20+ countries with enterprise clients"
    },
    {
      year: "2024",
      title: "Industry Leadership",
      description: "Recognized as leading enterprise AI solution provider"
    }
  ];

  /* ========== JSX ========== */
  return (
    <div className="bg-gray-50">
      {/* ---------- Hero ---------- */}
      <div className="relative w-full py-20 bg-gradient-to-br from-darkgray via-deepblue to-darkgray text-white">
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:50px_50px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl font-bold mb-6">
            Building the Future of Enterprise AI
          </h1>
          <p className="text-xl sm:text-2xl max-w-3xl mx-auto leading-relaxed opacity-90">
            We're not just consultants. We're builders, innovators, and partners in your AI transformation journey.
          </p>
        </div>
      </div>

      {/* ---------- Our Story ---------- */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                From Silicon Valley to Global Impact
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Founded by Silicon Valley veteran Ritesh Vajariya, AI Guru emerged from a simple observation: 
                enterprises needed more than just AI consultants—they needed builders who could deliver 
                production-ready solutions.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                With experience leading AI initiatives at AWS and Cerebras Systems, we understood the gap 
                between AI potential and enterprise reality. Today, we bridge that gap with custom solutions 
                that transform operations across industries.
              </p>
              <p className="text-lg text-gray-700">
                Our solutions power real-time translation for global financial services, intelligent 
                assistants for industrial operations, and revolutionary video localization platforms—all 
                built with enterprise-grade security and scalability.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.squarespace-cdn.com/content/v1/671f15a34acff55ddc02afbc/dec478a7-180a-4cb2-88e7-3cf12ccc2090/Ritesh-self-1.png?format=1500w"
                alt="Ritesh Vajariya"
                className="rounded-lg shadow-lg"
              />
              <img
                src="https://images.squarespace-cdn.com/content/v1/671f15a34acff55ddc02afbc/55bcce3b-7e94-4bf1-b0b5-174fac350e43/Ritesh-singapore-solo.png?format=1500w"
                alt="AI Guru in action"
                className="rounded-lg shadow-lg mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ---------- What Sets Us Apart ---------- */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Sets Us Apart
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We don't just advise—we build, deploy, and scale AI solutions that deliver measurable results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <Trophy className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Proven Track Record</h3>
              <p className="text-gray-700 mb-4">
                100+ successful enterprise implementations across Fortune 500 companies and industry leaders.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-sm text-gray-600">Average 3.2x ROI within 12 months</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-sm text-gray-600">95% client retention rate</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">End-to-End Delivery</h3>
              <p className="text-gray-700 mb-4">
                From concept to production in weeks, not months. We handle everything from architecture to deployment.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-sm text-gray-600">Custom development</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-sm text-gray-600">24/7 support & optimization</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Enterprise-Grade Security</h3>
              <p className="text-gray-700 mb-4">
                Bank-level security with compliance for GDPR, HIPAA, and industry-specific regulations.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-sm text-gray-600">SOC 2 compliant infrastructure</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-sm text-gray-600">End-to-end encryption</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Solutions Showcase ---------- */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Solutions That Transform Industries
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real implementations delivering measurable business impact
            </p>
          </div>

          <div className="space-y-12">
            {solutions.map((solution, index) => (
              <div key={index} className="flex flex-col md:flex-row items-center gap-8 p-8 bg-gray-50 rounded-2xl">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center text-white flex-shrink-0">
                  {solution.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{solution.title}</h3>
                  <p className="text-lg text-gray-700 mb-3">{solution.description}</p>
                  <div className="flex items-center text-green-600">
                    <TrendingUp className="w-5 h-5 mr-2" />
                    <span className="font-medium">{solution.impact}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/solution"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-lg hover:opacity-90 transition"
            >
              Explore All Solutions
              <ChevronRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ---------- Impact by Numbers ---------- */}
      <div className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-16">Our Impact in Numbers</h2>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {metrics.map((m, i) => (
              <div key={i} className="p-6">
                <div className="text-4xl md:text-5xl font-bold mb-2">
                  {m.number}
                </div>
                <p className="text-lg opacity-80">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ---------- Leadership ---------- */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-16">
            Leadership & Vision
          </h2>

          {team.map((member, i) => (
            <div key={i} className="max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-48 h-48 rounded-full object-cover shadow-lg"
                />
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">
                    {member.name}
                  </h3>
                  <p className="text-xl text-blue-600 font-medium mb-4">{member.role}</p>
                  <p className="text-lg text-gray-700 mb-6">{member.bio}</p>
                  <div className="space-y-2 mb-6">
                    {member.achievements.map((achievement, idx) => (
                      <div key={idx} className="flex items-start">
                        <Award className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">{achievement}</span>
                      </div>
                    ))}
                  </div>
                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-blue-600 hover:text-blue-800"
                  >
                    <span className="mr-2">Connect on LinkedIn</span>
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ---------- Our Journey Timeline ---------- */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-16">
            Our Journey
          </h2>
          
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gray-300"></div>
            {timeline.map((item, index) => (
              <div key={index} className={`relative flex items-center mb-12 ${index % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
                <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8'}`}>
                  <div className="bg-white p-6 rounded-lg shadow-lg">
                    <div className="text-2xl font-bold text-blue-600 mb-2">{item.year}</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-700">{item.description}</p>
                  </div>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-600 rounded-full"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Core Services ---------- */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Enterprise Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              End-to-end AI solutions from strategy to deployment and beyond
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {coreServices.map((service, i) => (
              <div
                key={i}
                className="bg-gray-50 p-8 rounded-lg hover:shadow-xl transition-shadow"
              >
                <div className="mx-auto flex items-center justify-center w-16 h-16 bg-white rounded-full mb-6 shadow-md">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ---------- Training as a Complement ---------- */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Empowering Teams Alongside Solutions
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                While our primary focus is building enterprise AI solutions, we also provide 
                comprehensive training to ensure your teams can maximize the value of AI investments.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                Our training programs, delivered to over 20,000 professionals globally, complement 
                our solution implementations by building internal AI capabilities.
              </p>
              <Link
                to="/courses"
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition"
              >
                Explore Training Programs
                <GraduationCap className="ml-2 w-5 h-5" />
              </Link>
            </div>
            <div>
              <img
                src="https://images.squarespace-cdn.com/content/v1/671f15a34acff55ddc02afbc/70e25ddf-3dc2-43a0-a7cc-d77329a10479/mexico-event-1.jpeg?format=1500w"
                alt="AI Training Session"
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Global Reach ---------- */}
      <div className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-4xl font-bold mb-6">Global Reach, Local Impact</h2>
            <p className="text-xl mb-8">
              Our solutions power enterprises across 20+ countries, with implementations 
              tailored to local regulations and business practices.
            </p>
            <div className="flex flex-wrap gap-3">
              {[
                "USA",
                "Singapore",
                "Mexico",
                "UK",
                "Canada",
                "India",
                "UAE",
                "Australia"
              ].map((c, i) => (
                <span
                  key={i}
                  className="px-3 py-1 bg-white/20 rounded-full text-sm"
                >
                  {c}
                </span>
              ))}
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm">
                +12 more
              </span>
            </div>
          </div>
          <div className="flex justify-center">
            <Globe className="w-64 h-64 text-white/80" />
          </div>
        </div>
      </div>

      {/* ---------- CTA ---------- */}
      <div className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-6">
            Ready to Transform Your Enterprise with AI?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Let's discuss how our proven solutions can address your specific challenges.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/solution"
              className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold py-3 px-8 rounded-lg hover:opacity-90 transition"
            >
              Explore Solutions
            </Link>
            <Link
              to="/contactUs"
              className="inline-block bg-white text-gray-900 font-bold py-3 px-8 rounded-lg hover:bg-gray-100 transition"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;
